import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { Toolbar } from "primereact/toolbar";
import { MultiSelect } from "primereact/multiselect";
import { useEffect, useRef, useState } from "react";
import { FilterMatchMode } from "primereact/api";
import AppBreadcrumb from "../../components/CustomeBreadcrumb/AppBreadcrumb";
import {
  bookingStatusDropdownItems,
  convertDateFrom,
  convertDateFromTo,
  defaultPageRowOptions,
  defaultRowOptions,
  paginatorLinks,
  showingEntries,
  SkeletonbodyTemplate,
  Skeletonitems,
  getUTCStartOfDay,
  getUTCEndOfDay
} from "../../appconfig/Settings";
import { getAllDetails, postAllData } from "../../service/ProductService";
import {
  ASSIGN_OPERATOR,
  GET_ASSIGN_OPERATORS,
  GET_BOOKING_LIST,
  GET_OPERATOR_LIST,
  GET_SERVICE_LIST,
  SET_BOOKING_STATUS,
} from "../../config/ApiConstant";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { bookingListCol } from "../../appconfig/DataTableColumns";
import {
  getBookingStatusSeverity,
  globalNameFilterTemplate,
} from "../../appconfig/AppHelper";
import { Badge } from "primereact/badge";
import { AssignOperatorSchema } from "../../config/Validate";
import { ErrorMessage, Form, Formik } from "formik";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { useNavigate } from "react-router";
import { amountTemplate, globalFieldTemplate } from "../../appconfig/TemplateSetting";
import { NavLink, useLocation } from "react-router-dom";
import { Avatar } from "primereact/avatar";

const BookingList = () => {
  document.title = "Bookings | Agriown";

  // formik ref
  const formikRef = useRef<any>(null);

  // navigation
  const navigate = useNavigate();
  const location = useLocation();

  //Set Toast/ Filter Properties
  const toast = useRef<any>(null);
  const [globalFilter, setGlobalFilter] = useState<any>("");
  const [statusFilterValue, setStatusFilterValue] = useState<any>(null);
  const [services, setServices] = useState<any[]>([]);
  const [serviceFilterValue, setServiceFilterValue] = useState<any>(null);

  //booking list state
  const [bookingList, setBookingList] = useState<any>([]);

  //Loading/Page Loading
  const [pageLoad, setPageLoad] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  // Date Object
  const [dates, setDates] = useState<string | Date | Date[] | any | null>([
    null,
    null,
  ]);

  // operator assign state
  const [assignDialogVisible, setAssignDialogVisible] =
    useState<boolean>(false);
  const [operatorList, setOperatorList] = useState<any[]>([]);
  const [assignOperatorList, setAssignOperatorList] = useState<any[]>([]);
  const [selectedBooking, setSelectedBooking] = useState<any>(null);

  // modal type state
  const [dialogType, setDialogType] = useState<any>("cancel");

  // datatable filter value sets
  const emptyFilters = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    farmer_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    service_name: { value: null, matchMode: FilterMatchMode.EQUALS },
    slot: { value: null, matchMode: FilterMatchMode.CONTAINS },
    total_amount: { value: null, matchMode: FilterMatchMode.CONTAINS },
    booking_status: { value: null, matchMode: FilterMatchMode.EQUALS },
    operator_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  };
  const [filters, setFilters] = useState(emptyFilters);

  // useefect for get last 3 month from current date
  useEffect(() => {
    const today = new Date();
    const state = location.state as any;
    if (state?.today) {
      setDates([today, today]);
    } else {
      const pastDate = new Date();
      pastDate.setMonth(today.getMonth() - 3);
      setDates([pastDate, today]);
    }
  }, []);

  // useefect get booking list api
  useEffect(() => {
    if (dates && dates[0] && dates[1]) {
      getBookingList(dates[0], dates[1]);
    } else if (!dates || dates.length === 0) {
      getBookingList();
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [dates]);

  // get services for filter
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await getAllDetails(GET_SERVICE_LIST);
        if (response) {
          setServices(response);
          setPageLoad(true);
        } else {
          setServices([]);
          setPageLoad(false);
        }
      } catch (error: any) {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error?.response?.data?.message,
        });
        setPageLoad(false);
      }
    };
    fetchServices();
  }, []);

  // get booking list api
  const getBookingList = async (startDate?: Date, endDate?: Date) => {
    try {
      let queryUrl = GET_BOOKING_LIST;

      if (startDate && endDate) {
        // taking start & end date and converting into UTC formated date
        const start_date = getUTCStartOfDay(startDate);
        const end_date = getUTCEndOfDay(endDate);
        queryUrl += `?start_date=${encodeURIComponent(start_date)}&end_date=${encodeURIComponent(end_date)}`;
      }

      const response = await getAllDetails(queryUrl);

      if (response) {
        setBookingList(response);
        setPageLoad(true);
      } else {
        setBookingList([]);
        setPageLoad(false);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
      setPageLoad(false);
    }
  };

  // export csv
  const exportCSV = () => {
    if (!bookingList?.length) {
      toast.current?.show({
        severity: "warn",
        summary: "Warning",
        detail: "No data available to export",
      });
      return;
    }

    const csvData = bookingList.map((item: any) => {
      const total = item?.total_amount ?? 0;

      const paid =
        item?.payment_data?.reduce(
          (acc: number, p: any) => acc + (p.amount_paid || 0),
          0
        ) ?? 0;

      const remaining = Math.max(total - paid, 0);

      return {
        "Farmer Name": item?.farmer_name || "-",
        "Mobile": item?.mobile || "-",
        "Service Name": item?.service_name || "-",
        "Total Farm": item?.booking_farm_data?.length || 0,
        "Booking Date": item?.booking_date
          ? convertDateFromTo(item.booking_date)
          : "-",
        "Total Amount": total,
        "Paid Amount": paid,
        "Remaining Amount": remaining,
        "Status": item?.booking_status || "-",
        "Operator Name":
          item?.booking_status === "Pending"
            ? "Yet to Assign"
            : item?.operator_name || "-",
      };
    });

    const csvRows = [];

    // headers
    const headers = Object.keys(csvData[0]);
    csvRows.push(headers.join(","));

    // rows
    csvData.forEach((row: any) => {
      const values = headers.map((header) =>
        `"${String(row[header]).replace(/"/g, '""')}"`
      );
      csvRows.push(values.join(","));
    });

    const csvString = csvRows.join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });

    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `booking-list-${new Date().getTime()}.csv`
    );

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // global search or    button
  const leftToolbarTemplate = () => {
    return (
      <>
        <span className="mr-2 md:mr-3 search-bar">
          <i className="pi pi-search"></i>
          <InputText
            type="search"
            className="search-input"
            value={globalFilter}
            onInput={(e) => setGlobalFilter(e.currentTarget.value)}
            placeholder="Search..."
          />
        </span>
      </>
    );
  };

  //right toolbar
  const rightToolbarTemplate = () => {
    return (
      <>
        <Button
          icon="pi pi-file-excel"
          label="Export CSV"
          className="p-button-success mr-3"
          onClick={exportCSV}
        />
        <Calendar
          value={dates}
          className="mr-2 md:mr-3 order-cle"
          dateFormat="dd/mm/yy"
          onChange={(e) => setDates(e.value)}
          selectionMode="range"
          showIcon
          readOnlyInput
        />
        <Button
          icon="pi pi-filter-slash"
          label="Clear"
          onClick={() => clearAll()}
          className="md:mr-2 p-button-outlined p-button-primary"
        ></Button>
        <Button
          label="Add Booking"
          icon="pi pi-plus"
          className="ml-2 p-button toolbar-button"
          onClick={() => navigate("/booking/add-booking")}
        />
      </>
    );
  };

  //Clear All Filter
  const clearAll = () => {
    setFilters({ ...emptyFilters });
    setGlobalFilter("");
    setStatusFilterValue(null);
    setServiceFilterValue(null);
    setDates(null);
  };

  // close dialog
  const handleCloseAssignDialog = () => {
    setAssignDialogVisible(false);
    setOperatorList([]);
    setAssignOperatorList([]);
    setSelectedBooking(null);
    setDialogType(null);
  };

  //get operator list api
  const fetchOperatorList = async (serviceId: any) => {
    try {
      const response = await getAllDetails(
        `${GET_OPERATOR_LIST}?type=dropdown&service_id=${serviceId}`
      );
      if (response) {
        setOperatorList(response);
      } else {
        setOperatorList([]);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    }
  };

  // get selected operator api
  const fetchSelectedOperator = async (bookingId: any) => {
    try {
      const response = await getAllDetails(
        `${GET_ASSIGN_OPERATORS}?booking_id=${bookingId}`
      );

      if (response) {
        setAssignOperatorList(
          response.map((item: any) => ({
            id: item.operator_data.id,
            name: item.operator_data.name,
          }))
        );
      } else {
        setAssignOperatorList([]);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    }
  };

  // handle operator assign dialog and operator list api
  const handleOpenAssignDialog = (booking: any) => {
    setSelectedBooking(booking);
    setDialogType("assign");
    setAssignDialogVisible(true);
    fetchOperatorList(booking?.service_id);
  };

  // post api for assign operator and status change for cancel and reschedule
  const onSubmit = async (values: any = {}, formikHelpers: any = {}) => {
    const resetForm = formikHelpers?.resetForm || (() => { });
    try {
      setLoading(true);
      let response;

      if (dialogType === "assign") {
        // Assign operator API
        const payload = {
          booking_schedule_date: convertDateFrom(values?.selected_date),
          booking_id: selectedBooking.id,
          operator_id: values.operator_id.map((id: any) => ({ id })),
        };

        response = await postAllData(ASSIGN_OPERATOR, payload);
      } else if (dialogType === "reschedule") {
        //  Reschedule API
        const payload: any = {
          booking_id: selectedBooking.id,
          type: dialogType,
          booking_reschedule_date: convertDateFrom(values?.selected_date),
        };
        if (selectedBooking?.operator_name && selectedBooking?.isReschedule) {
          payload.operator_id = [];
        } else {
          payload.operator_id = values.operator_id.map((id: any) => ({ id }));
        }

        response = await postAllData(SET_BOOKING_STATUS, payload);
      } else if (dialogType === "cancel") {
        const payload = {
          booking_id: selectedBooking,
          type: dialogType,
        };
        response = await postAllData(SET_BOOKING_STATUS, payload);
      }

      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.message,
        });

        if (dialogType === "assign" || dialogType === "reschedule") resetForm();
        setAssignDialogVisible(false);
        getBookingList(dates[0], dates[1]);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // modal footer content
  const footerContent = (
    <>
      <Button
        label={
          dialogType === "assign" || dialogType === "reschedule"
            ? "Cancel"
            : "No"
        }
        icon="pi pi-times"
        onClick={handleCloseAssignDialog}
        className="p-button-secondary"
      />

      <Button
        label={
          dialogType === "assign" || dialogType === "reschedule"
            ? "Save"
            : "Yes"
        }
        icon="pi pi-check"
        type="button"
        loading={loading}
        disabled={loading}
        onClick={() =>
          dialogType === "assign" || dialogType === "reschedule"
            ? formikRef.current?.handleSubmit()
            : onSubmit()
        }
        autoFocus
      />
    </>
  );

  // handle open cancel dialog
  const handleOpenCancelDialog = (rowData: any) => {
    setSelectedBooking(rowData?.id);
    setDialogType("cancel");
    setAssignDialogVisible(true);
  };

  // handle open reschedule dialog
  const handleOpenReschduleDialog = (rowData: any) => {
    setSelectedBooking(rowData);
    setDialogType("reschedule");
    setAssignDialogVisible(true);
    if (
      rowData?.booking_status.toLowerCase() === "pending" &&
      rowData?.isReschedule
    ) {
      fetchOperatorList(rowData?.service_id);
    } else if (
      rowData?.booking_status.toLowerCase() === "reschedule" &&
      rowData?.isReschedule &&
      !rowData?.operator_name
    ) {
      fetchOperatorList(rowData?.service_id);
      fetchSelectedOperator(rowData.id);
    }
  };

  //Change action Template
  const actionBodyTemplate = (rowData: any) => (
    <div className="actions">
      <Button
        icon="pi pi-user"
        className="mr-2 p-button-secondary"
        tooltip="Assign Operator"
        disabled={
          !(
            rowData?.booking_status?.toLowerCase() === "pending" ||
            (rowData?.booking_status?.toLowerCase() === "reschedule" &&
              window.cb(rowData?.operator_name))
          )
        }
        tooltipOptions={{ position: "top" }}
        onClick={() => handleOpenAssignDialog(rowData)}
      />

      <Button
        icon="pi pi-eye"
        className="mr-2 p-button-secondary"
        tooltip="View Detail"
        tooltipOptions={{ position: "top" }}
        onClick={() => navigate(`/booking/detail/${btoa(rowData.id)}`)}
      />
      {(rowData?.isReschedule || rowData?.isCancelled) && (
        <Button
          icon={rowData?.isCancelled ? "pi pi-times" : "pi pi-calendar"}
          className="mr-2 p-button-secondary"
          tooltip={
            rowData?.isCancelled ? "Cancel Booking" : "Reschedule Booking"
          }
          tooltipOptions={{ position: "top" }}
          onClick={() =>
            rowData?.isCancelled
              ? handleOpenCancelDialog(rowData)
              : handleOpenReschduleDialog(rowData)
          }
        />
      )}
    </div>
  );

  // time slot template
  const slotTemplate = (rowData: any) => (
    <p>
      {rowData?.time_slot_data?.slot} ({rowData?.time_slot_data?.start_time} -{" "}
      {rowData?.time_slot_data?.end_time})
    </p>
  );

  //order status template
  const orderStatusTemplate = (rowData: any) => {
    return (
      <Badge
        value={rowData.booking_status}
        severity={getBookingStatusSeverity(rowData.booking_status)}
      />
    );
  };

  // mobile number template
  const mobileNumberTemplate = (rowData: any) => {
    return <p>{rowData?.mobile ? rowData?.mobile : "-"}</p>;
  };

  //filter by status dropdown
  const filterByStatus = (options: any) => {
    return (
      <Dropdown
        filter
        value={statusFilterValue}
        options={bookingStatusDropdownItems}
        onChange={(e) => {
          setStatusFilterValue(e.value);
          options.filterCallback(e.value ? e.value : null, options.index);
        }}
        optionLabel="name"
        optionValue="code"
        placeholder="Select Status"
        className="order-drp"
        showClear
      />
    );
  };

  // filter by service dropdown
  const filterByService = (options: any) => {
    return (
      <Dropdown
        filter
        value={serviceFilterValue}
        options={services}
        onChange={(e) => {
          setServiceFilterValue(e.value);
          options.filterCallback(e.value ? e.value : null, options.index);
        }}
        optionLabel="service_name"
        optionValue="service_name"
        placeholder="Select Service"
        className="order-drp"
        showClear
      />
    );
  };

  //name Template
  const nameTemplate = (rowData: any) => {
    return (
      <>
        <NavLink to={`/farmer/detail/${btoa(rowData?.farmer_id)}`}>
          <div className="flex avatar-div align-items-center">
            {!window.cn(rowData?.farmer_img) ? (
              <Avatar image={rowData?.farmer_img} shape="circle" />
            ) : (
              <div className="img-fluid d-flex align-items-center">
                <Avatar
                  label={rowData?.farmer_name?.charAt(0).toUpperCase()}
                  style={{
                    backgroundColor: "#438440",
                    color: "#ffffff",
                  }}
                  shape="circle"
                />
              </div>
            )}
            <div className="ml-2 emp-name link-name">{rowData.farmer_name}</div>
          </div>
        </NavLink>
      </>
    );
  };

  // operator name template
  const operatorNameTemplate = (rowData: any) => {
    return (
      <p
        style={{
          color:
            rowData?.booking_status === "Pending" ||
              rowData?.booking_status === "PreConfirmed" ||
              (rowData?.booking_status === "Reschedule" &&
                !window.cb(rowData?.operator_name))
              ? "#fc6161"
              : "",
        }}
      >
        {rowData?.booking_status === "Pending"
          ? "Yet to Assign"
          : rowData?.booking_status === "PreConfirmed"
            ? "Yet to Approve"
            : rowData?.booking_status === "Reschedule" &&
              !window.cb(rowData?.operator_name)
              ? "Reschedule Requested"
              : rowData?.operator_name}
      </p>
    );
  };

  // transaction amount template
  const transactionAmountTemplate = (rowData: any) => {
    const total = rowData?.total_amount ?? 0;

    const paid =
      rowData?.payment_data?.reduce(
        (acc: number, p: any) => acc + (p.amount_paid || 0),
        0
      ) ?? 0;

    const remaining = Math.max(total - paid, 0);
    return (
      <div style={{ lineHeight: "1.4" }}>
        <div className="flex align-items-center gap-1 justify-content-center">
          <span>Paid Amount:</span>{" "}
          {amountTemplate({ paid_amount: paid }, { field: "paid_amount" })}
        </div>
        <div className="flex align-items-center gap-1 justify-content-center">
          <span>Remaining Amount:</span>{" "}
          {amountTemplate(
            { remaining_amount: remaining },
            { field: "remaining_amount" }
          )}
        </div>
      </div>
    );
  };

  // total farm template
  const totalFarmTemplate = (rowData: any) => {
    return (
      <span>
        {!window.cn(rowData?.booking_farm_data)
          ? rowData?.booking_farm_data.length
          : "-"}
      </span>
    );
  };

  return (
    <>
      {/* Breadcrumb */}
      <AppBreadcrumb
        currentMenu="Booking"
        pageName="Booking List"
        pageTitle="Booking List"
      />

      <div className="grid crud-demo">
        <div className="col-12">
          <div className="card">
            <Toast ref={toast} />

            <Toolbar
              left={leftToolbarTemplate}
              right={rightToolbarTemplate}
            ></Toolbar>

            {pageLoad ? (
              <>
                <DataTable
                  value={bookingList}
                  showGridlines
                  stripedRows
                  globalFilter={globalFilter || null}
                  rows={defaultRowOptions}
                  paginator={bookingList.length >= 10 && true}
                  rowsPerPageOptions={defaultPageRowOptions}
                  className="datatable-responsive p-datatble-sm"
                  paginatorTemplate={paginatorLinks}
                  currentPageReportTemplate={showingEntries}
                  emptyMessage="No Data Found"
                  filters={{ ...filters, global: { ...filters.global, value: globalFilter || null } }}
                  onFilter={(e: any) => setFilters(e.filters)}
                  removableSort
                >
                  {bookingListCol.map((col, i) => {
                    if (col.field === "action") {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          header={col.header}
                          body={actionBodyTemplate}
                        />
                      );
                    } else if (col.field === "time_slot_data") {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          body={slotTemplate}
                          header={col.header}
                        />
                      );
                    } else if (col.field === "farmer_name") {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          sortable
                          body={nameTemplate}
                          filter
                          filterElement={globalNameFilterTemplate}
                          header={col.header}
                        />
                      );
                    } else if (col.field === "service_name") {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          sortable
                          body={globalFieldTemplate}
                          filter
                          filterElement={filterByService}
                          header={col.header}
                        />
                      );
                    } else if (col.field === "operator_name") {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          sortable
                          filter
                          body={operatorNameTemplate}
                          filterElement={globalNameFilterTemplate}
                          header={col.header}
                        />
                      );
                    } else if (col.field === "transaction") {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          body={transactionAmountTemplate}
                          header={col.header}
                        />
                      );
                    } else if (col.field === "total_amount") {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          body={amountTemplate}
                          header={col.header}
                        />
                      );
                    } else if (col.field === "total_farm") {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          body={totalFarmTemplate}
                          header={col.header}
                        />
                      );
                    } else if (col.field === "booking_date") {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          header={col.header}
                          body={(rowData) =>
                            rowData.booking_date
                              ? convertDateFromTo(rowData.booking_date)
                              : "-"
                          }
                        />
                      );
                    } else if (col.field === "booking_status") {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          filter
                          filterElement={filterByStatus}
                          body={orderStatusTemplate}
                          header={col.header}
                        />
                      );
                    } else if (col.field === "mobile") {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          body={mobileNumberTemplate}
                          header={col.header}
                        />
                      );
                    } else {
                      return (
                        <Column
                          key={col.field}
                          field={col.field}
                          header={col.header}
                        />
                      );
                    }
                  })}
                </DataTable>
              </>
            ) : (
              <>
                {/* Skeleton Data table */}
                <DataTable value={Skeletonitems}>
                  {bookingListCol.map((col, i) => (
                    <Column
                      key={col.field}
                      field={col.field}
                      header={col.header}
                      body={SkeletonbodyTemplate}
                    />
                  ))}
                </DataTable>
              </>
            )}
          </div>
        </div>
      </div>

      <Dialog
        visible={assignDialogVisible}
        style={{ width: "450px" }}
        header={
          dialogType === "cancel"
            ? "Cancel Booking"
            : dialogType === "reschedule"
              ? "Reschedule Booking"
              : "Assign Operator"
        }
        draggable={false}
        modal
        className="mx-3 lg:mx-0"
        onHide={handleCloseAssignDialog}
        footer={footerContent}
      >
        {dialogType === "assign" || dialogType === "reschedule" ? (
          <Formik
            enableReinitialize
            innerRef={formikRef}
            initialValues={{
              operator_id:
                dialogType === "reschedule" && assignOperatorList.length > 0
                  ? assignOperatorList.map((n) => n.id)
                  : [],
              selected_date: selectedBooking
                ? new Date(
                  dialogType === "assign"
                    ? selectedBooking.booking_date
                    : selectedBooking.booking_reschedule_date
                )
                : null,
              booking_status: selectedBooking?.booking_status ?? "", //extra for validate
              isReschedule: selectedBooking?.isReschedule ?? false, //extra for validate
              operator_name: selectedBooking?.operator_name,
            }}
            validationSchema={AssignOperatorSchema}
            onSubmit={onSubmit}
          >
            {({
              values,
              errors,
              touched,
              setFieldValue,
              handleBlur,
              handleSubmit,
            }) => (
              <Form onSubmit={handleSubmit}>
                <div className="grid p-fluid">
                  {/* Date Picker */}
                  <div className="field col-12">
                    <label htmlFor="date">
                      {dialogType === "assign"
                        ? "Booking Date"
                        : "Reschedule Date"}
                    </label>
                    <Calendar
                      id="date"
                      name="date"
                      value={values.selected_date}
                      onChange={(e) => setFieldValue("selected_date", e.value)}
                      onBlur={handleBlur}
                      minDate={
                        selectedBooking
                          ? new Date(
                            dialogType === "assign"
                              ? selectedBooking.booking_date
                              : selectedBooking.booking_reschedule_date
                          )
                          : new Date()
                      }
                      showIcon
                      dateFormat="dd/mm/yy"
                    />
                  </div>
                  {
                    // selectedBooking?.booking_status.toLowerCase() ===
                    //   "Reschedule" &&
                    // selectedBooking?.isReschedule
                    selectedBooking?.operator_name ? (
                      ""
                    ) : (
                      <div className="field col-12">
                        <label htmlFor="operator">Select Operators</label>
                        <MultiSelect
                          id="operator"
                          name="operator"
                          value={values.operator_id}
                          options={operatorList}
                          onChange={(e: any) =>
                            setFieldValue("operator_id", e.value)
                          }
                          onBlur={handleBlur}
                          className={
                            touched.operator_id && errors.operator_id
                              ? "p-invalid"
                              : ""
                          }
                          optionLabel="name"
                          optionValue="id"
                          placeholder="Choose Operators"
                          filter
                          display="chip"
                        />
                        <small className="p-error">
                          <ErrorMessage name="operator_id" />
                        </small>
                      </div>
                    )
                  }
                </div>
              </Form>
            )}
          </Formik>
        ) : (
          <div>
            <p>
              Are you sure you want to{" "}
              {dialogType === "cancel" ? "cancel" : "reschedule"} this booking?
            </p>
          </div>
        )}
      </Dialog>
    </>
  );
};

export default BookingList;
